import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Package, User, Calendar, FileText, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { brandSpecsManager, authManager } from '../mock';
import { useToast } from '../hooks/use-toast';

const ViewOnlyBrandSpecs = () => {
  const { id, brandName, brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const currentUser = authManager.getCurrentUser();
  const isAdmin = currentUser?.role === 'Admin';

  useEffect(() => {
    const loadBrand = () => {
      try {
        let brandData = null;
        
        if (brandId) {
          // Look up by Brand ID (primary method)
          brandData = brandSpecsManager.getBrandByBrandId(brandId);
        } else if (brandName) {
          // Look up by brand name (fallback)
          const decodedBrandName = decodeURIComponent(brandName);
          brandData = brandSpecsManager.getBrandByName(decodedBrandName);
        } else if (id) {
          // Look up by internal ID (backward compatibility)
          brandData = brandSpecsManager.getBrandById(parseInt(id));
        }
        
        if (brandData) {
          setBrand(brandData);
        } else {
          toast({
            title: 'Brand Not Found',
            description: 'The requested brand specification could not be found.',
            variant: 'destructive'
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load brand specification.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    if (id || brandName || brandId) {
      loadBrand();
    }
  }, [id, brandName, brandId, toast]);

  const handleDownload = async (format = 'json') => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can download brand specifications.',
        variant: 'destructive'
      });
      return;
    }

    try {
      let result;
      if (format === 'pdf') {
        result = await brandSpecsManager.downloadBrandPDF(brand.id);
      } else {
        result = brandSpecsManager.downloadBrandById(brand.id);
      }
      
      if (result.success) {
        toast({
          title: 'Download Complete',
          description: result.message,
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: error.message || 'Failed to download brand specification.',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading brand specification...</p>
        </div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Brand Not Found</h1>
          <p className="text-gray-600 mb-6">The requested brand specification could not be found.</p>
          <Link to="/orders">
            <Button variant="outline">Back to Orders</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div>
            <div className="flex items-center mb-2">
              <Link to="/orders" className="mr-3">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Orders
                </Button>
              </Link>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{brand.brandName}</h1>
            <p className="text-gray-600">Brand Code: {brand.brandCode}</p>
          </div>
          
          <div className="flex space-x-3">
            {isAdmin && (
              <>
                <Button
                  onClick={() => handleDownload('pdf')}
                  variant="outline"
                  className="flex items-center"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => handleDownload('json')}
                  variant="outline"
                  className="flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download JSON
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Specifications */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Client Details */}
            <Card>
              <CardHeader>
                <CardTitle>Client Details</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Brand Name</label>
                  <p className="text-gray-900">{brand.brandName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Brand Code</label>
                  <p className="text-gray-900">{brand.brandCode}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Buyer Company</label>
                  <p className="text-gray-900">{brand.buyerCompany}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Contact Person</label>
                  <p className="text-gray-900">{brand.contactPerson || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Phone</label>
                  <p className="text-gray-900">{brand.clientPhone || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <p className="text-gray-900">{brand.clientEmail || 'Not specified'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Bag Specifications */}
            <Card>
              <CardHeader>
                <CardTitle>Bag Specifications</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Bag Length</label>
                  <p className="text-gray-900">{brand.bagLength}"</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Bag Width</label>
                  <p className="text-gray-900">{brand.bagWidth}"</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Bag Weight</label>
                  <p className="text-gray-900">{brand.bagWeight || 'Not specified'} gms</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Construction</label>
                  <p className="text-gray-900">{brand.bagConstruction || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Top</label>
                  <p className="text-gray-900">{brand.top}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Bottom</label>
                  <p className="text-gray-900">{brand.bottom}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Handle Type</label>
                  <p className="text-gray-900">{brand.handleType || 'None'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Closure Type</label>
                  <p className="text-gray-900">{brand.closureType || 'Not specified'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Material Specifications */}
            <Card>
              <CardHeader>
                <CardTitle>Material Specifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* PP Woven Fabric */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">PP Woven Fabric Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Fabric Variant</label>
                        <p className="text-gray-900">{brand.fabricVariant || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Fabric Color</label>
                        <p className="text-gray-900">{brand.fabricColor || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Fabric Grams (GSM)</label>
                        <p className="text-gray-900">{brand.fabricGrams || brand.fabricGSM || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* BOPP Details */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-3">BOPP Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Printing Material</label>
                        <p className="text-gray-900">{brand.printingMaterial || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">BOPP Micron</label>
                        <p className="text-gray-900">{brand.boppMicron || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">BOPP Film Size</label>
                        <p className="text-gray-900">{brand.boppFilmSize ? `${brand.boppFilmSize}mm` : 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Metallic Properties */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Metallic Properties</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Metallic</label>
                        <p className="text-gray-900">{brand.metallic || 'No'}</p>
                      </div>
                      {brand.metallic === 'Yes' && (
                        <>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Metallic Microns</label>
                            <p className="text-gray-900">{brand.metallicMicrons || 'Not specified'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Metallic Film Size</label>
                            <p className="text-gray-900">{brand.metallicFilmSize ? `${brand.metallicFilmSize}mm` : 'Not specified'}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Window and Additional Options */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Additional Options</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Window</label>
                        <p className="text-gray-900">{brand.window || 'No'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Lamination</label>
                        <p className="text-gray-900">{brand.lamination || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Gusset</label>
                        <p className="text-gray-900">{brand.gusset || 'No'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Handle</label>
                        <p className="text-gray-900">{brand.handle || 'No'}</p>
                      </div>
                    </div>
                    {brand.window === 'Yes' && (
                      <div className="mt-4">
                        <label className="text-sm font-medium text-gray-600">Window Film Size</label>
                        <p className="text-gray-900">{brand.windowMetallicFilmSize ? `${brand.windowMetallicFilmSize}mm` : 'Not specified'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cylinder Details */}
            <Card>
              <CardHeader>
                <CardTitle>Cylinder Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Printing Area Width</label>
                    <p className="text-gray-900">{brand.printingAreaWidth ? `${brand.printingAreaWidth}mm` : 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Printing Area Circumference</label>
                    <p className="text-gray-900">{brand.printingAreaCircum ? `${brand.printingAreaCircum}mm` : 'Not specified'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Cylinder Size Width</label>
                    <p className="text-gray-900">{brand.cylinderSizeWidth ? `${brand.cylinderSizeWidth}mm` : 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Cylinder Size Circumference</label>
                    <p className="text-gray-900">{brand.cylinderSizeCircum ? `${brand.cylinderSizeCircum}mm` : 'Not specified'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Number of Cylinders</label>
                    <p className="text-gray-900">{brand.numberOfCylinders || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">White Cylinder</label>
                    <p className="text-gray-900">{brand.whiteCylinder || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Perforation</label>
                    <p className="text-gray-900">{brand.perforation || 'No'}</p>
                  </div>
                </div>
                {brand.commonCylinder && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Common Cylinder Notes</label>
                    <p className="text-gray-900">{brand.commonCylinder}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Status & Information */}
            <Card>
              <CardHeader>
                <CardTitle>Status & Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <div className="mt-1">
                    <Badge 
                      variant={brand.status === 'Active' ? 'default' : 'secondary'}
                      className={brand.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                    >
                      {brand.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Created Date</label>
                  <div className="flex items-center text-gray-900 mt-1">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(brand.createdDate).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Last Modified</label>
                  <p className="text-gray-900">{brand.lastModified}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Created By</label>
                  <div className="flex items-center text-gray-900 mt-1">
                    <User className="w-4 h-4 mr-2" />
                    {brand.createdBy}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Brand Artwork */}
            <Card>
              <CardHeader>
                <CardTitle>Brand Artwork</CardTitle>
              </CardHeader>
              <CardContent>
                {brand.artworkImageUrl ? (
                  <div>
                    <img 
                      src={brand.artworkImageUrl} 
                      alt={`${brand.brandName} artwork`}
                      className="w-full h-auto rounded-lg border border-gray-200"
                    />
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No artwork uploaded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewOnlyBrandSpecs;